// generatedAppContainment.js — the AST gate for AI-generated workspace apps.
//
// The rules are data in generatedAppRules.js (the server reads the same file).
// This module parses the EXACT text the freestyle parser evaluates (acorn — a
// runtime dependency of the shared package already, no regex) and refuses:
//   • any free identifier outside GENERATED_APP_GLOBALS (window, self, top,
//     frames, opener, document, fetch, Function, eval, … fall out by
//     construction), resolved per lexical scope so a local `let self` in one
//     function never hides a global `self` in another; `arguments` always;
//   • GENERATED_APP_BLOCKED_MEMBERS as a member, a destructured key or a
//     literal computed key, any `__`-prefixed member, `.apply` / `.bind`, and
//     a computed member key that is not a literal;
//   • `.call` anywhere except `x.call('<allowlisted literal>', …)`;
//   • on an EVENT handle (first parameter of a DOMQL `on<Event>` handler) any
//     member outside GENERATED_APP_EVENT_MEMBERS (`e.view` is the window), and
//     on `e.target` any member outside GENERATED_APP_TARGET_MEMBERS;
//   • on an ELEMENT handle (`el`, `this`) any member outside
//     GENERATED_APP_ELEMENT_MEMBERS — in-shell every shell method is a
//     property of every element, `el.sdk` included;
//   • a handle that escapes: an event / element / `this` may only be the object
//     of a member read or an argument to an allowlisted `el.call`;
//   • `x.root` not followed by a key that passes the seed deny list, and any
//     write (assignment, update, delete, state write method) under a `.root`
//     chain — in-shell `s.root` is the LIVE workspace root;
//   • reflective members of globals (Object.getPrototypeOf, …) and string
//     literals naming GENERATED_APP_BLOCKED_STRINGS;
//   • `with`, tagged templates, `import()`, `import.meta`, `new.target`,
//     setTimeout / setInterval with anything but a function;
//   • and the same inside any string that parses as a function (a
//     JSON-authored source carries its handlers as strings that the parser
//     destringifies and evaluates).
// The auditor itself is @symbo.ls/app-rules (smbls packages/app-rules), pinned
// in package.json: generatedAppAudit.js + generatedAppRules.js are exact
// copies of its src/audit.js + src/rules.js (only the import path differs);
// generatedAppContainment.test.js holds them equal to the installed package. This
// module keeps the WORKSPACE half: the allowlist / readable root keys / live
// registry it hands the audit, the iframe registry, and the parse wrapper.
import * as appRules from './generatedAppRules.js'
import {
  designSystemColorExamples,
  generatedViewStyleLint,
  pageCssVarResolver
} from './generatedViewStyleLint.js'
import {
  GENERATED_APP_BLOCKED,
  auditGeneratedAppExpression,
  generatedAppAuditHook,
  generatedAppBlockedError
} from './generatedAppAudit.js'

export {
  GENERATED_APP_BLOCKED,
  auditGeneratedAppExpression,
  generatedAppAuditHook,
  generatedAppBlockedError
}

// Every rule generatedAppAudit.js reads off its `appRules` namespace, with its type
// (generatedAppContainment.test.js holds this list equal to the audit's reads).
// The audit is only as good as `appRules`: when a build mis-binds the namespace
// (measured 2026-10-03 — frank bound the shell's `R` to an inlined library's
// minified helper `function R(n)`, so `R.GENERATED_APP_BLOCKED_PREFIX` read
// undefined and `el.__ref` passed), a missing list is an EMPTY list and the
// audit fails OPEN. So every entry point checks the shape first and refuses
// when it does not hold — fail closed, never open.
const GENERATED_APP_RULE_SHAPE = Object.freeze({
  APP_EL_CALL_ALLOWLIST: 'list',
  GENERATED_APP_BLOCKED_MEMBERS: 'list',
  GENERATED_APP_BLOCKED_STRINGS: 'list',
  GENERATED_APP_BLOCKED_INDIRECTION: 'list',
  GENERATED_APP_BLOCKED_PREFIX: 'text',
  GENERATED_APP_GLOBALS: 'list',
  GENERATED_APP_GLOBAL_MEMBERS: 'map',
  GENERATED_APP_EVENT_MEMBERS: 'list',
  GENERATED_APP_EVENT_VALUE_MEMBERS: 'list',
  GENERATED_APP_TARGET_MEMBERS: 'list',
  GENERATED_APP_TARGET_VALUE_MEMBERS: 'list',
  GENERATED_APP_TARGET_DEEP_MEMBERS: 'list',
  GENERATED_APP_TARGET_DEEP_CHAIN: 'map',
  GENERATED_APP_ELEMENT_MEMBERS: 'list',
  GENERATED_APP_LIFECYCLE_HANDLERS: 'list',
  GENERATED_APP_ROOT_DENY_KEYS: 'list',
  GENERATED_APP_ROOT_DENY_PATTERN: 'text',
  GENERATED_APP_ROOT_WRITE_METHODS: 'list',
  GENERATED_APP_TAG_ALLOWLIST: 'list',
  GENERATED_APP_COMPONENT_ALLOWLIST: 'list',
  GENERATED_APP_COMPONENT_DENYLIST: 'list',
  GENERATED_APP_MARKUP_KEYS: 'list',
  GENERATED_APP_URL_KEYS: 'list',
  GENERATED_APP_URL_SCHEMES: 'list',
  GENERATED_APP_CSS_URL_KEYS: 'list',
  GENERATED_APP_SRC_DATA_PREFIX: 'text'
})

// true only when `rules` carries every rule above with its type (a non-empty
// list of strings, a non-empty string, a plain object). Any throw is false.
export const generatedAppRulesIntact = (rules = appRules) => {
  try {
    if (!rules || typeof rules !== 'object') return false
    for (const [key, kind] of Object.entries(GENERATED_APP_RULE_SHAPE)) {
      const v = rules[key]
      if (kind === 'list') {
        if (!Array.isArray(v) || v.length === 0 || !v.every((x) => typeof x === 'string'))
          return false
      } else if (kind === 'text') {
        if (typeof v !== 'string' || v.length === 0) return false
      } else if (!v || typeof v !== 'object' || Array.isArray(v)) return false
    }
    return true
  } catch (_) {
    return false
  }
}

// The el.call names a page may use: THE one list (generatedAppRules.js
// APP_EL_CALL_ALLOWLIST, the server's list). `root` is accepted for callers
// and ignored: no module loader, AI verb or app-local function joins it.
// Rules that did not load allow nothing.
//
// The body sits in a module helper because generatedAppAllowlist is ALSO a
// shell function entry (el.call): frank emits a function entry that names the
// `appRules` namespace as a value with a free `generatedAppRules_exports` identifier
// (a ReferenceError when called), while a lifted helper reaches `appRules` through
// its scope (workspacePushEmission.test.js holds both forms).
const _allowlistFromRules = () =>
  generatedAppRulesIntact(appRules) ? [...appRules.APP_EL_CALL_ALLOWLIST] : []
export const generatedAppAllowlist = (root) => _allowlistFromRules()

// The `s.root` keys a page may read: the active module's catalog stateKeys and
// its loaders' write keys — the keys the AI is told carry data (uiCatalog.js).
export const generatedAppRootKeys = (root) => {
  const project = root && root.workspaceProject
  const keys = new Set(Array.isArray(project && project.stateKeys) ? project.stateKeys : [])
  const loaders =
    project && project.loaders && typeof project.loaders === 'object' ? project.loaders : {}
  for (const meta of Object.values(loaders)) {
    for (const k of Array.isArray(meta && meta.writes) ? meta.writes : []) keys.add(k)
  }
  return [...keys]
}

// The function registry an iframe-mounted generated app gets: ONLY the
// allowlisted names that exist, never the shell's ctx.functions.
export const scopeGeneratedAppFunctions = (functions, allow) => {
  const out = {}
  if (!functions) return out
  for (const name of allow) if (typeof functions[name] === 'function') out[name] = functions[name]
  return out
}

const _rootStateOf = (el) =>
  (el && el.getRootState && el.getRootState()) ||
  (el && el.state && (el.state.root || el.state)) ||
  (el && el.getRoot && el.getRoot() && el.getRoot().state) ||
  null

// One sentence per KIND of refusal, for the model that wrote the source
// (render_answer_ui and the builders return it as `hint`): told only the rule
// ids, it retried the same shape — Storm on dev 2026-10-04: render_answer_ui
// was refused on six of ten calls, extending the Validators app's own ValCard
// / ValCellMid and touching `node`; after the catalog fix one refusal was left,
// for `Badge` — a shell component the reviewed list does not carry. Lives HERE, not in generatedAppAudit.js
// (a parity copy of the published @symbo.ls/app-rules audit). Pure; empty
// when there is nothing to say.
export function generatedAppViolationHint(violations, extra = {}) {
  const list = Array.isArray(violations) ? violations : []
  const rules = new Set(list.map((v) => v && v.rule).filter(Boolean))
  const named = (rule) =>
    [
      ...new Set(
        list
          .filter((v) => v && v.rule === rule && v.detail && !/^\(/.test(String(v.detail)))
          .map((v) => String(v.detail))
      )
    ].slice(0, 6)
  const parts = []
  if (rules.has('component_not_allowed')) {
    const names = named('component_not_allowed')
    parts.push(
      (names.length ? names.join(', ') + ': ' : '') +
        'not on the list of components a generated view mounts (an installed app’s own ' +
        'components never are) — build it from Flex, Grid, Box, Text, Hgroup, Icon, Img, Link ' +
        'and Button (and the components the source defines in its own `components:`), with ' +
        'literal `extends` names.'
    )
  }
  // ANSWER-VIEW-UNABLE-TO-CREATE-THE-SUMMARY-VIEW-1 — measured on dev
  // 2026-10-05 (Storm Validators, "why is this amber?"): the view put each
  // row's shape in an inline `childExtends: { … }` object and keyed its data
  // by `node` (`{ node: 'storm-chi-1' }`, `s.node`). Told only "literal
  // `extends` names" and "no `node`", the model read a technical failure and
  // gave up. Each refusal now names its fix.
  if (list.some((v) => v && v.rule === 'component_not_allowed' && v.detail === '(computed)')) {
    parts.push(
      '`extends` / `childExtends` take a quoted component NAME (or a list of names) — never an ' +
        'inline object or an expression. To shape every child alike, write the source as ' +
        "`{ components: { FleetRow: { extends: 'Flex', … } }, page: { …, childExtends: " +
        "'FleetRow' } }` with a distinctive name of your own."
    )
  }
  if (rules.has('blocked-key') || rules.has('blocked-member')) {
    const names = [...new Set([...named('blocked-key'), ...named('blocked-member')])]
    parts.push(
      (names.length ? names.join(', ') + ': ' : '') +
        'a generated view never reaches the DOM, the window or the shell — no `node`, ' +
        '`document`, `window` or storage; use DOMQL props, `text`, `children` and state.'
    )
    // A refused OBJECT KEY is the view's own data or prop name — a reserved
    // word everywhere in the source, not only on an element.
    const keys = named('blocked-key')
    if (keys.length) {
      parts.push(
        keys.map((k) => '`' + k + '`').join(', ') +
          ' is reserved even as a data field or a state key — rename it everywhere it ' +
          "appears, in the data and where it is read (`{ node: 'x' }` / `s.node` → " +
          "`{ host: 'x' }` / `s.host`)."
      )
    }
  }
  if (rules.has('computed-key')) {
    parts.push('Object keys and member names must be literal names, never computed.')
  }
  // A url-capable style key (background, backgroundImage, mask, cursor, …)
  // takes a LITERAL only — a function / template that computes it is refused
  // ('css-url', or 'url-dynamic' when JSON-authored) since it could form a
  // url(). Measured on dev 2026-10-05 (Storm, "treasury share bars"): three
  // refusals for `background: (el, s) => s.barColor`, told only "no url()" or
  // nothing, and the model gave up. A per-row colour has a dynamic home.
  const cssUrlKeys = new Set(appRules.GENERATED_APP_CSS_URL_KEYS || [])
  const styleUrl = [
    ...new Set(
      list
        .filter(
          (v) =>
            v &&
            (v.rule === 'css-url' || v.rule === 'url-dynamic') &&
            cssUrlKeys.has(String(v.detail))
        )
        .map((v) => String(v.detail))
    )
  ]
  // a literal url() to an unsafe address (the detail is not a key)
  if (list.some((v) => v && v.rule === 'css-url' && !cssUrlKeys.has(String(v.detail)))) {
    parts.push('No url() in styles — use design-system tokens and Img with a src.')
  }
  if (styleUrl.length) {
    parts.push(
      styleUrl.join(', ') +
        ': a style key that can carry url() takes a literal value only — no url() to an ' +
        'unsafe address and no function or template that computes it. For a colour ' +
        'computed per row use `backgroundColor` (or `color`, `borderColor`), which may be ' +
        'a function.'
    )
  }
  const linkUrl = named('url-dynamic').filter((k) => !cssUrlKeys.has(k))
  if (linkUrl.length) {
    parts.push(
      linkUrl.join(', ') +
        ': a link or image address is a literal (https:, a relative path), a ' +
        'same-origin path with the row appended to a leading literal ' +
        "('/chains/chain?id=' + r.id), or an icon name — never a bare row field, " +
        "never a function, and never '//' or another origin with the row appended."
    )
  }
  // GENERATED-VIEW-STYLE-LINT-1 — sizes and colours that render nothing.
  if (rules.has('unitless-size')) {
    const at = [
      ...new Set(list.filter((v) => v && v.rule === 'unitless-size').map((v) => v.at))
    ].slice(0, 6)
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        'a bare number is not a size and renders nothing — use a spacing token ' +
        "('X', 'Y', 'Z', 'A', 'B2') or a CSS length with its unit ('8px', '50%')."
    )
  }
  // GENERATED-VIEW-SIZE-WORD-1 — `size: 'xl'` on a heading boxed its text in
  // a one-letter-tall box; the lines overflowed onto the next one.
  if (rules.has('size-word')) {
    const at = [...new Set(list.filter((v) => v && v.rule === 'size-word').map((v) => v.at))].slice(
      0,
      6
    )
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        '`size` sets the box (width and height), never the type size — a word like ' +
        "'xl' squeezes the text into a tiny box and it spills over the line below. A type " +
        "size is `fontSize` with a type token ('Z', 'A', 'B', 'C'); drop `size` from text."
    )
  }
  // GENERATED-VIEW-GRID-COLS-1 — `cols: 2` on a Grid drew one column.
  if (rules.has('grid-cols')) {
    const at = [...new Set(list.filter((v) => v && v.rule === 'grid-cols').map((v) => v.at))].slice(
      0,
      6
    )
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        '`cols` is not a DOMQL prop — it is ignored and the grid draws one column. Write ' +
        '`columns` (or `gridTemplateColumns`) as a track STRING: two equal columns are ' +
        "'repeat(2, 1fr)', a label + value pair 'C 1fr' (a spacing token is a track too)."
    )
  }
  if (rules.has('grid-track')) {
    const at = [
      ...new Set(list.filter((v) => v && v.rule === 'grid-track').map((v) => v.at))
    ].slice(0, 6)
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        'a grid track list is a STRING of tracks — never a number, an array or an object ' +
        "(the grid falls back to one column): three equal columns are 'repeat(3, 1fr)', a " +
        "table's columns '2fr 1fr 1fr 2fr'; children fill the cells in order."
    )
  }
  if (rules.has('unknown-css-var')) {
    const vars = named('unknown-css-var')
    const near = [
      ...new Set(
        list
          .filter((v) => v && v.rule === 'unknown-css-var' && v.near)
          .map((v) => `${v.detail} → ${v.near}`)
      )
    ].slice(0, 4)
    parts.push(
      (vars.length ? vars.join(', ') + ': ' : '') +
        'not a CSS variable this workspace defines, so it resolves to nothing — never guess a ' +
        "variable: write the design system's own names (color: 'title', background: " +
        "'blue.2', padding: 'A'); its variables read --color-<name> and --spacing-<token>" +
        (near.length ? ' (did you mean ' + near.join(', ') + '?)' : '') +
        '.'
    )
  }
  if (rules.has('unknown-theme')) {
    const names = named('unknown-theme')
    const themes = Array.isArray(extra.themeExamples) ? extra.themeExamples.slice(0, 6) : []
    parts.push(
      (names.length ? names.map((n) => `'${n}'`).join(', ') + ': ' : '') +
        "not a theme of this design system (it is ignored) — a text colour is `color: '<name>'`, " +
        'not a theme' +
        (themes.length ? '; its themes are ' + themes.map((n) => `'${n}'`).join(', ') : '') +
        '.'
    )
  }
  // GENERATED-VIEW-HGROUP-PLACEHOLDER-TEXT-1 — an Hgroup draws only its H and
  // P slots; anything else shows nothing or the workspace's placeholder copy.
  if (rules.has('hgroup-empty') || rules.has('hgroup-placeholder')) {
    const at = [
      ...new Set(
        list
          .filter((v) => v && (v.rule === 'hgroup-empty' || v.rule === 'hgroup-placeholder'))
          .map((v) => v.at)
      )
    ].slice(0, 6)
    const instead = [
      ...new Set(
        list
          .filter((v) => v && v.rule === 'hgroup-empty' && v.detail && !/^Hgroup/.test(v.detail))
          .flatMap((v) => String(v.detail).split(', '))
      )
    ]
    const copy = [
      ...new Set(
        list.filter((v) => v && v.rule === 'hgroup-placeholder').map((v) => String(v.detail))
      )
    ]
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        'an Hgroup draws only its own `H` (the heading) and `P` (the line under it)' +
        (instead.length
          ? ' — it has no ' + instead.map((k) => '`' + k + '`').join(' / ') + ' prop'
          : '') +
        ". Write `{ extends: 'Hgroup', H: { text: … }, P: { text: … } }`, and `P: { hide: " +
        'true }` when there is no line under the heading' +
        (copy.length
          ? ' — a slot left out shows the placeholder copy (' + copy.join('; ') + ')'
          : '') +
        '. A list of rows is a Flex, not an Hgroup.'
    )
  }
  if (rules.has('unknown-color')) {
    const names = named('unknown-color')
    const examples = Array.isArray(extra.colorExamples) ? extra.colorExamples.slice(0, 8) : []
    parts.push(
      (names.length ? names.map((n) => `'${n}'`).join(', ') + ': ' : '') +
        'not a colour of this design system, so it is dropped (a text colour falls back, ' +
        'a background turns transparent) — use one of its colours' +
        (examples.length ? ' (' + examples.map((n) => `'${n}'`).join(', ') + ', …)' : '') +
        " or a CSS colour; an alpha or a shade joins the name with no space: 'blue.2', " +
        "'blue-10', 'blue+10'."
    )
  }
  // GENERATED-VIEW-PALETTE-FILL-AND-UNKNOWN-ICON-1 — measured on dev 2026-10-05
  // (Storm, "a small answer view that summarizes fleet health"): `{ theme:
  // 'card', backgroundColor: 'cerulean-10' }` painted the accent over the card
  // theme's own ink — the body text read 1.3:1, the title 2.4:1 — and an Icon
  // named 'symbols/support' drew the "no icon" placeholder.
  if (rules.has('palette-fill')) {
    const at = [
      ...new Set(list.filter((v) => v && v.rule === 'palette-fill').map((v) => v.at))
    ].slice(0, 6)
    const themes = Array.isArray(extra.themeExamples) ? extra.themeExamples.slice(0, 6) : []
    parts.push(
      (at.length ? at.join(', ') + ': ' : '') +
        'a palette colour as the background of an element that carries TEXT keeps whatever ' +
        'ink was already in force, so the text reads at about 1.3:1 — a fill needs its ' +
        'paired ink. Use a theme, which sets the background AND the colour' +
        (themes.length
          ? " (this system's themes are " + themes.map((n) => `'${n}'`).join(', ') + ')'
          : '') +
        '. A `color` of its own is not a pair — one ink over an accent fill still misses ' +
        'the contrast floor. An accent colour belongs on a small mark — a dot, a bar, an ' +
        'icon — never on a text plate.'
    )
  }
  if (rules.has('unknown-icon')) {
    const names = named('unknown-icon')
    const near = [
      ...new Set(
        list
          .filter((v) => v && v.rule === 'unknown-icon' && v.near)
          .map((v) => `${v.detail} → ${v.near}`)
      )
    ].slice(0, 4)
    parts.push(
      (names.length ? names.map((n) => `'${n}'`).join(', ') + ': ' : '') +
        'not an icon this design system registers, so the Icon draws the "no icon" ' +
        'placeholder — write a name the system carries, with no `library/` prefix (the ' +
        'resolver never strips one)' +
        (near.length ? ' (did you mean ' + near.join(', ') + '?)' : '') +
        '.'
    )
  }
  // A styling refusal on a BAR (the commonest one — measured on dev
  // 2026-10-05: six "treasury share bars" runs refused for the bar's own
  // height / colour / variable, and none reached for the reviewed component):
  // name the component that needs neither.
  const STYLE_RULES = ['unitless-size', 'unknown-color', 'unknown-css-var', 'unknown-theme']
  // …or a colour / variable refusal on a background, the bar's own paint
  // (measured: anonymous `children.3.children.0.style.backgroundColor` bars).
  const onBar = list.some(
    (v) =>
      v &&
      STYLE_RULES.includes(v.rule) &&
      (/(^|\.)(\w*Bar|Fill|Track|Progress)\w*\./.test(String(v.at)) ||
        (v.rule !== 'unitless-size' && /(^|\.)background(Color)?$/.test(String(v.at))))
  )
  if (onBar) {
    parts.push(
      "A share, progress or usage bar is the reviewed `{ extends: 'ProgressBar', value: 0.46 }` " +
        '(value 0–1) — it needs no height, colour or variable of its own.'
    )
  }
  return parts.join(' ')
}

// Runs `parserName` (a registered freestyle entry that REQUIRES an audit hook)
// with the containment gate as its hook, and maps every outcome to one shape:
// { ok: true, value, allow } | { ok: false, blocked: true, error, violations }
// | { ok: false, error }. FAIL CLOSED when the rules did not load, and when
// the parser ran no audit (an older parser that ignores the option).
const _gatedFreestyle = (el, parserName, source) => {
  if (!generatedAppRulesIntact(appRules)) {
    return {
      ok: false,
      blocked: true,
      error: GENERATED_APP_BLOCKED,
      violations: [
        { rule: 'rules-unavailable', at: null, detail: 'the containment rules did not load' }
      ]
    }
  }
  const root = _rootStateOf(el)
  const allow = generatedAppAllowlist(root)
  const ctx =
    (el && el.context) || (el && el.getRoot && el.getRoot() && el.getRoot().context) || null
  // The local workspace picker controls shell navigation. Keep it blocked
  // even before a lazy registry arrives; never expose it to generated views.
  const registeredComponents = [
    ...new Set([
      ...(ctx && ctx.components ? Object.keys(ctx.components) : []),
      'AssistantFlyWorkspace',
      'PhoneNavigation',
      'PhoneNavButton',
      'PhoneAppSection',
      'PhoneAppTile'
    ])
  ]
  const gate = generatedAppAuditHook(allow, generatedAppRootKeys(root), registeredComponents)
  let audited = false
  const hook = (expr) => {
    audited = true
    gate(expr)
  }
  let value
  try {
    value = el.call(parserName, source, { audit: hook })
  } catch (e) {
    // a syntax error alone is a parse error for the model to fix, not a
    // blocked feature (nothing evaluated either way)
    if (
      e &&
      e.code === GENERATED_APP_BLOCKED &&
      (e.violations || []).every((v) => v.rule === 'parse')
    ) {
      return {
        ok: false,
        error: 'parse: ' + ((e.violations[0] && e.violations[0].detail) || 'syntax')
      }
    }
    if (e && e.code === GENERATED_APP_BLOCKED) {
      const violations = e.violations || []
      const hint = generatedAppViolationHint(violations)
      return {
        ok: false,
        blocked: true,
        error: GENERATED_APP_BLOCKED,
        violations,
        ...(hint ? { hint } : {})
      }
    }
    return { ok: false, error: 'parse: ' + ((e && e.message) || String(e)) }
  }
  if (!audited) {
    return {
      ok: false,
      blocked: true,
      error: GENERATED_APP_BLOCKED,
      violations: [{ rule: 'audit-unavailable', at: null, detail: 'the parser ran no audit' }]
    }
  }
  return { ok: true, value, allow }
}

// el.call('parseGeneratedAppSource', source) → { ok, def, allow } |
// { ok: false, blocked: true, error, violations } | { ok: false, error }.
// THE parse every in-shell generated-app path uses (the four workspace
// builders, canvas answerUi). The audit runs inside parseFreestyleSource on
// the exact text it evaluates.
//
// `opts.lint` (render_answer_ui): the parsed view is also held to the style
// rules that render NOTHING, or render UNREADABLY, when broken — a bare number
// as a size, a colour the live design system does not have, a palette fill with
// no ink paired to it, an icon name the system does not register
// (generatedViewStyleLint.js) — and is refused with a hint naming each one, so
// the next call draws it right.
export function parseGeneratedAppSource(source, opts) {
  const el = this
  const r = _gatedFreestyle(el, 'parseFreestyleSource', source)
  if (!r.ok) return r
  if (opts && opts.lint && r.value && typeof r.value === 'object') {
    const ctx =
      (el && el.context) || (el && el.getRoot && el.getRoot() && el.getRoot().context) || null
    const ds = ctx && ctx.designSystem
    const page = r.value.page && typeof r.value.page === 'object' ? r.value.page : r.value
    const violations = generatedViewStyleLint(page, ds, {
      cssVarDefined: pageCssVarResolver(),
      components: (ctx && ctx.components) || null,
      // the framework's own name transform, so the icon rule resolves a name
      // exactly the way the Icon component does (never a second copy of it)
      toCamelCase: (ctx && ctx.utils && ctx.utils.toCamelCase) || undefined
    })
    if (violations.length) {
      const hint = generatedAppViolationHint(violations, {
        colorExamples: designSystemColorExamples(ds),
        themeExamples: Object.keys((ds && (ds.theme || ds.THEME)) || {}).slice(0, 6)
      })
      return {
        ok: false,
        blocked: true,
        error: GENERATED_APP_BLOCKED,
        violations,
        ...(hint ? { hint } : {})
      }
    }
  }
  return { ok: true, def: r.value, allow: r.allow }
}

// el.call('auditGeneratedAppSource', source) → { ok, expr, jsonAuthored, allow }
// | the same refusals. The SANDBOX path (ISOLATED-RUNTIME-PLAN §8.1): the same
// gate on the same exact text, and nothing evaluates in the shell realm — the
// opaque frame evaluates `expr`.
export function auditGeneratedAppSource(source) {
  const r = _gatedFreestyle(this, 'auditFreestyleSource', source)
  if (!r.ok) return r
  const v = r.value || {}
  if (typeof v.expr !== 'string') {
    return { ok: false, error: 'parse: the parser returned no expression text' }
  }
  return { ok: true, expr: v.expr, jsonAuthored: v.jsonAuthored === true, allow: r.allow }
}
